import React, { useEffect } from 'react';
import { Stack } from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import { DefaultButton, PrimaryButton } from 'common/components/Buttons/v8';
import { Heading3, Text } from 'common/components/Text';
import { useLocalizedStrings } from 'i18n';

import { useNotificationSettingsConfirmationTokens } from '../NotificationSettings.styles';

export interface INotificationSettingsConfirmationProps {
  /**
   * Title for the confirmation panel
   */
  title: string;
  /**
   * Sub-text to show beneath the title of the confirmation panel
   */
  subText: string;
  /**
   * Closes the confirmation panel
   */
  onClose: () => void;
  /**
   * Handles the confirm button click
   */
  onConfirm: () => void;
}

export const NotificationSettingsConfirmation: React.FC<INotificationSettingsConfirmationProps> = (
  props: INotificationSettingsConfirmationProps
) => {
  const { title, subText, onClose, onConfirm } = props;
  const tokens = useNotificationSettingsConfirmationTokens();
  const { t } = useLocalizedStrings();
  const headingId = useId('panel-heading-');

  useEffect(() => {
    // Note: We need to focus on the heading element for screen readers to correctly focus on and read the title out loud.
    // Without this, the screen reader will focus on and read the app/page name instead.
    document.getElementById(headingId)?.focus();
  }, [headingId]);

  return (
    <Stack tokens={tokens.wrapper}>
      <Heading3 variant="Title1Strong" id={headingId} tabIndex={-1}>
        {title}
      </Heading3>
      <Text>{subText}</Text>
      <Stack horizontal tokens={tokens.footer}>
        <PrimaryButton
          onClick={onClose}
          aria-describedby={headingId}
          text={t('OfficeShellPanel.NotificationSettings.Sections.TurnOffConfirm.LeaveOn')}
        />
        <DefaultButton
          onClick={onConfirm}
          aria-describedby={headingId}
          text={t('OfficeShellPanel.NotificationSettings.Sections.TurnOffConfirm.TurnOff')}
        />
      </Stack>
    </Stack>
  );
};
